/**
 * Messages Index - Internationalization Support
 * This file provides a unified interface for accessing messages in different locales
 */

import * as enUS from "./en_US.js";
import * as enUK from "./en_UK.js";
import * as esES from "./es_ES.js";

// Available locales
export const AVAILABLE_LOCALES = {
  "en-US": enUS,
  "en-UK": enUK,
  "es-ES": esES,
};

// Default locale
export const DEFAULT_LOCALE = "en-US";

// Current locale (can be set dynamically)
let currentLocale = DEFAULT_LOCALE;

/**
 * Set the current locale
 * @param {string} locale - The locale code (e.g., 'en-US', 'es-ES')
 */
export const setLocale = (locale) => {
  if (AVAILABLE_LOCALES[locale]) {
    currentLocale = locale;
  } else {
    console.warn(`Locale '${locale}' not found. Available locales:`, Object.keys(AVAILABLE_LOCALES));
  }
};

/**
 * Get the current locale
 * @returns {string} Current locale code
 */
export const getCurrentLocale = () => currentLocale;

/**
 * Get messages for the current locale
 * @returns {object} Messages object for current locale
 */
export const getCurrentMessages = () => {
  return AVAILABLE_LOCALES[currentLocale] || AVAILABLE_LOCALES[DEFAULT_LOCALE];
};

/**
 * Get error message by key path for current locale
 * @param {string} keyPath - Dot-separated key path (e.g., 'TEMPLATE.FRIENDLY_NAME_REQUIRED')
 * @returns {string} Error message
 */
export const getErrorMessage = (keyPath) => {
  const messages = getCurrentMessages();
  return messages.getErrorMessage(keyPath);
};

/**
 * Get success message by key path for current locale
 * @param {string} keyPath - Dot-separated key path (e.g., 'TEMPLATE.ADDED')
 * @returns {string} Success message
 */
export const getSuccessMessage = (keyPath) => {
  const messages = getCurrentMessages();
  return messages.getSuccessMessage(keyPath);
};

/**
 * Get confirmation message by key path for current locale
 * @param {string} keyPath - Dot-separated key path (e.g., 'TEMPLATE.DELETE_CONFIRMATION')
 * @returns {string} Confirmation message
 */
export const getConfirmationMessage = (keyPath) => {
  const messages = getCurrentMessages();
  return messages.getConfirmationMessage(keyPath);
};

/**
 * Get all error messages for current locale
 * @returns {object} Error messages object
 */
export const getErrorMessages = () => {
  const messages = getCurrentMessages();
  return messages.ERROR_MESSAGES;
};

/**
 * Get all success messages for current locale
 * @returns {object} Success messages object
 */
export const getSuccessMessages = () => {
  const messages = getCurrentMessages();
  return messages.SUCCESS_MESSAGES;
};

/**
 * Get all confirmation messages for current locale
 * @returns {object} Confirmation messages object
 */
export const getConfirmationMessages = () => {
  const messages = getCurrentMessages();
  return messages.CONFIRMATION_MESSAGES;
};

// Export current locale messages for backward compatibility
const currentMessages = getCurrentMessages();
export const {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  CONFIRMATION_MESSAGES,
  TEMPLATE_ERRORS,
  VALIDATION,
  MESSAGE_ERRORS,
  GENERAL_ERRORS,
  PHONE,
  USER_ERRORS,
  TEAMS_ERRORS,
  TEMPLATE_SUCCESS,
  MESSAGE_SUCCESS,
  GENERAL_SUCCESS,
  USER_SUCCESS,
  TEAMS_SUCCESS,
  TEMPLATE_CONFIRMATION,
  GENERAL_CONFIRMATION,
} = currentMessages;
